/* eslint-disable @typescript-eslint/no-explicit-any */
import { FabricObject, ActiveSelection } from 'fabric';

import { Marker } from '../../metaBullets/classes/Marker';

/**
 * Проверяет является ли объект маркером
 */
export function isMarker(obj: FabricObject | any): obj is Marker {
  return obj && (obj.isMarker === true || obj.type === 'marker');
}

/**
 * Проверяет является ли выделение только маркерами
 * @param selection - ActiveSelection или одиночный объект
 * @returns true если выделение содержит только маркеры
 */
export function isSelectionOnlyMarkers(selection: FabricObject | any): boolean {
  if (!selection) {
    return false;
  }

  // Если это ActiveSelection (группа объектов)
  if (selection.type?.toLowerCase() === 'activeselection') {
    const objects = (selection as ActiveSelection)._objects || [];

    // Проверяем что все объекты - маркеры
    if (objects.length === 0) {
      return false;
    }

    const allAreMarkers = objects.every((obj) => isMarker(obj));
    const hasMarkers = objects.some((obj) => isMarker(obj));

    console.debug('🎯 Checking if ActiveSelection contains only markers:', {
      totalObjects: objects.length,
      allAreMarkers,
      hasMarkers,
      objectTypes: objects.map((obj: any) => ({
        id: obj.id,
        type: obj.type,
        isMarker: obj.isMarker,
      })),
    });

    return allAreMarkers;
  }

  // Если это одиночный объект
  return isMarker(selection);
}

/**
 * Проверяет содержит ли выделение хотя бы один маркер (но не только маркеры)
 * @param selection - ActiveSelection или одиночный объект
 * @returns true если есть маркеры, но не все объекты - маркеры
 */
export function hasMarkersInMixedSelection(selection: FabricObject | any): boolean {
  if (!selection) {
    return false;
  }

  // Если это ActiveSelection (группа объектов)
  if (selection.type?.toLowerCase() === 'activeselection') {
    const objects = (selection as ActiveSelection)._objects || [];

    if (objects.length === 0) {
      return false;
    }

    const hasMarkers = objects.some((obj) => isMarker(obj));
    const allAreMarkers = objects.every((obj) => isMarker(obj));

    return hasMarkers && !allAreMarkers;
  }

  return false;
}

/**
 * Получает информацию о выделенных маркерах
 * @param selection - ActiveSelection или одиночный объект
 * @returns массив выделенных маркеров
 */
export function getSelectedMarkers(selection: FabricObject | any): Marker[] {
  if (!selection) {
    return [];
  }

  // Если это ActiveSelection (группа объектов)
  if (selection.type?.toLowerCase() === 'activeselection') {
    const objects = (selection as ActiveSelection)._objects || [];
    return objects.filter((obj) => isMarker(obj)) as Marker[];
  }

  // Если это одиночный маркер
  if (isMarker(selection)) {
    return [selection as Marker];
  }

  return [];
}

/**
 * Получает ID сущностей связанных с выделенными маркерами
 * @param selection - ActiveSelection или одиночный объект
 * @returns массив entityId для выделенных маркеров
 */
export function getMarkerEntityIds(selection: FabricObject | any): string[] {
  const markers = getSelectedMarkers(selection);
  return markers.map((marker) => marker.entityId).filter((id) => id !== undefined);
}

/**
 * Блокирует трансформации для выделения содержащего маркеры
 * - Если группа содержит ТОЛЬКО маркеры: блокирует движение, масштабирование, поворот
 * - Если группа содержит маркеры + другие объекты: блокирует только масштабирование и поворот
 * @param selection - ActiveSelection или одиночный объект
 */
export function lockMarkerTransformations(selection: FabricObject | any): void {
  if (!selection) {
    return;
  }

  const onlyMarkers = isSelectionOnlyMarkers(selection);
  const hasMarkers = hasMarkersInMixedSelection(selection) || onlyMarkers;

  if (!hasMarkers) {
    return;
  }

  if (onlyMarkers) {
    // Если только маркеры - блокируем всё
    selection.set({
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true,
      lockScalingFlip: true, // 🔒 Блокируем изменение ширины/высоты через flip
      lockRotation: true,
      hasControls: false,
    });

    console.debug('🔒 Locked all transformations for marker-only selection:', {
      selectionType: selection.type,
      objectsCount: selection._objects?.length || 1,
    });
  } else {
    // Если есть маркеры + другие объекты - блокируем масштабирование, поворот и изменение размеров
    selection.set({
      lockScalingX: true,
      lockScalingY: true,
      lockScalingFlip: true, // 🔒 Блокируем изменение ширины/высоты через flip
      lockRotation: true,
      // Движение разрешено
      lockMovementX: false,
      lockMovementY: false,
    });

    console.debug('🔒 Locked scaling, rotation and flip for mixed selection with markers:', {
      selectionType: selection.type,
      objectsCount: selection._objects?.length || 1,
    });
  }
}
